/**
 * Recent Posts Slider
 * Simple fade transition implementation for displaying recent posts
 */

class RecentPostsSlider {
    constructor() {
        this.currentIndex = 0;
        this.posts = [];
        this.autoplayInterval = null;
        this.progressAnimation = null;
        this.progressStartTime = null;
        this.progressDuration = 5000; // 5 seconds
        this.init();
    }

    init() {
        const container = document.querySelector('.recent-posts-slider');
        
        if (!container) {
            return;
        }

        const postsData = container.getAttribute('data-posts');
        if (!postsData) {
            return;
        }

        this.posts = JSON.parse(postsData);
        this.titleElement = container.querySelector('.post-title');
        this.excerptElement = container.querySelector('.post-excerpt');
        this.linkElement = container.querySelector('.post-link');
        this.prevButton = container.querySelector('.nav-prev');
        this.nextButton = container.querySelector('.nav-next');
        this.progressPath = container.querySelector('.progress-path');

        // console.log('Progress path found:', this.progressPath);

        if (this.posts.length === 0) {
            return;
        }

        this.setupEventListeners();
        this.initProgressIndicator();
        this.showPost(0);
        this.startAutoplay();
    }

    setupEventListeners() {
        this.prevButton?.addEventListener('click', () => {
            this.showPrevious();
        });

        this.nextButton?.addEventListener('click', () => {
            this.showNext();
        });
    }

    showPost(index) {
        if (!this.posts[index]) {
            return;
        }

        this.currentIndex = index;
        const post = this.posts[index];

        // Reset progress indicator
        this.resetProgress();

        // Fade out
        this.titleElement.style.opacity = '0';
        if (this.excerptElement) {
            this.excerptElement.style.opacity = '0';
        }

        setTimeout(() => {
            // Update content
            this.titleElement.textContent = post.title;
            if (this.excerptElement) {
                // Always update excerpt, even if empty
                this.excerptElement.textContent = post.excerpt || '';
            }
            this.linkElement.href = post.permalink;

            // Fade in
            this.titleElement.style.opacity = '1';
            if (this.excerptElement) {
                this.excerptElement.style.opacity = '1';
            }

            // Start progress animation after fade in completes
            setTimeout(() => {
                this.startProgressAnimation();
            }, 250);
        }, 250);
    }

    showNext() {
        const nextIndex = (this.currentIndex + 1) % this.posts.length;
        this.showPost(nextIndex);
        this.restartAutoplay();
    }

    showPrevious() {
        const prevIndex = (this.currentIndex - 1 + this.posts.length) % this.posts.length;
        this.showPost(prevIndex);
        this.restartAutoplay();
    }

    startAutoplay() {
        this.autoplayInterval = setInterval(() => {
            this.showNext();
        }, 5000);
    }

    stopAutoplay() {
        if (this.autoplayInterval) {
            clearInterval(this.autoplayInterval);
            this.autoplayInterval = null;
        }
        this.stopProgressAnimation();
    }

    restartAutoplay() {
        this.stopAutoplay();
        this.startAutoplay();
    }

    initProgressIndicator() {
        if (!this.progressPath) {
            return;
        }

        // Calculate the circumference of the circle (2 * π * radius)
        const radius = 8; // radius from the SVG
        this.pathLength = 2 * Math.PI * radius;
        
        // Add buffer to ensure complete fill
        this.pathLength = this.pathLength + 10;
        
        // Start with no visible stroke for animation
        this.progressPath.style.strokeDasharray = `${this.pathLength} ${this.pathLength}`;
        this.progressPath.style.strokeDashoffset = this.pathLength;
        
        // Make sure the stroke is visible
        this.progressPath.style.stroke = 'url(#paint0_linear_2933_12159)';
        this.progressPath.style.strokeWidth = '2';
        this.progressPath.style.opacity = '1';
        
        // console.log('Progress initialized');
    }

    startProgressAnimation() {
        this.stopProgressAnimation();
        this.progressStartTime = Date.now();
        this.animateProgress();
    }

    stopProgressAnimation() {
        if (this.progressAnimation) {
            cancelAnimationFrame(this.progressAnimation);
            this.progressAnimation = null;
        }
    }

    resetProgress() {
        if (this.progressPath) {
            this.progressPath.style.strokeDasharray = `${this.pathLength} ${this.pathLength}`;
            this.progressPath.style.strokeDashoffset = this.pathLength;
            // console.log('Progress reset - setting to 0 dash');
        }
    }

    animateProgress() {
        const currentTime = Date.now();
        const elapsed = currentTime - this.progressStartTime;
        const progress = Math.min(elapsed / this.progressDuration, 1);
        
        // Calculate the dash offset to make it go clockwise
        const offset = this.pathLength * (1 - progress);
        
        this.progressPath.style.strokeDasharray = `${this.pathLength} ${this.pathLength}`;
        this.progressPath.style.strokeDashoffset = offset;
        
        // console.log(`Progress: ${(progress * 100).toFixed(1)}%, Offset: ${offset.toFixed(1)}`);
        
        if (progress < 1) {
            this.progressAnimation = requestAnimationFrame(() => this.animateProgress());
        }
    }
}

export default RecentPostsSlider;